<template>
  <div class="p-6 max-w-2xl mx-auto">
    <!-- Title -->
    <h2 class="text-2xl font-semibold text-center mb-8">
      Financial Ratio Calculator
    </h2>

    <!-- Inputs -->
    <form @submit.prevent="calculate">
      <!-- Current Assets -->
      <div class="mb-6">
        <label class="block mb-1 font-medium">Current Assets (₹)</label>
        <BaseInput v-model.number="currentAssets" placeholder="e.g. 100000" />
      </div>

      <!-- Current Liabilities -->
      <div class="mb-6">
        <label class="block mb-1 font-medium">Current Liabilities (₹)</label>
        <BaseInput v-model.number="currentLiabilities" placeholder="e.g. 50000" />
      </div>

      <!-- Inventory -->
      <div class="mb-6">
        <label class="block mb-1 font-medium">Inventory (₹)</label>
        <BaseInput v-model.number="inventory" placeholder="e.g. 20000" />
      </div>

      <!-- Total Liabilities -->
      <div class="mb-6">
        <label class="block mb-1 font-medium">Total Liabilities (₹)</label>
        <BaseInput v-model.number="totalLiabilities" placeholder="e.g. 150000" />
      </div>

      <!-- Shareholder Equity -->
      <div class="mb-6">
        <label class="block mb-1 font-medium">Shareholder Equity (₹)</label>
        <BaseInput v-model.number="totalEquity" placeholder="e.g. 100000" />
      </div>

      <!-- Net Income -->
      <div class="mb-6">
        <label class="block mb-1 font-medium">Net Income (₹)</label>
        <BaseInput v-model.number="netIncome" placeholder="e.g. 20000" />
      </div>

      <!-- Buttons -->
      <div class="button-row flex justify-end">
        <BaseButton
          type="button"
          variant="secondary"
          @click="reset"
        >
          Clear
        </BaseButton>
        <BaseButton
          type="submit"
          variant="primary"
        >
          Calculate
        </BaseButton>
      </div>
    </form>

    <!-- Results -->
    <div v-if="calculated" class="mt-8">
      <h3 class="text-xl font-semibold mb-4 text-center">Results</h3>
      <div class="results-grid">
        <div class="result-card">
          <p class="text-sm text-gray-600">Current Ratio</p>
          <p class="text-lg font-medium">{{ currentRatio.toFixed(2) }}</p>
        </div>
        <div class="result-card">
          <p class="text-sm text-gray-600">Quick Ratio</p>
          <p class="text-lg font-medium">{{ quickRatio.toFixed(2) }}</p>
        </div>
        <div class="result-card">
          <p class="text-sm text-gray-600">Debt-Equity Ratio</p>
          <p class="text-lg font-medium">{{ debtEquityRatio.toFixed(2) }}</p>
        </div>
        <div class="result-card">
          <p class="text-sm text-gray-600">Return on Equity (ROE)</p>
          <p class="text-lg font-medium">{{ returnOnEquity.toFixed(2) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import BaseInput  from '@/components/ui/BaseInput.vue';
import BaseButton from '@/components/ui/BaseButton.vue';

export default {
  name: 'FinancialRatioCalculatorView',
  components: { BaseInput, BaseButton },

  setup() {
    const currentAssets      = ref(0);
    const currentLiabilities = ref(0);
    const inventory          = ref(0);
    const totalLiabilities   = ref(0);
    const totalEquity        = ref(0);
    const netIncome          = ref(0);
    const calculated         = ref(false);

    const currentRatio = computed(() =>
      currentLiabilities.value
        ? currentAssets.value / currentLiabilities.value
        : 0
    );
    const quickRatio = computed(() =>
      currentLiabilities.value
        ? (currentAssets.value - inventory.value) / currentLiabilities.value
        : 0
    );
    const debtEquityRatio = computed(() =>
      totalEquity.value
        ? totalLiabilities.value / totalEquity.value
        : 0
    );
    const returnOnEquity = computed(() =>
      totalEquity.value
        ? netIncome.value / totalEquity.value
        : 0
    );

    function calculate() {
      calculated.value = true;
    }
    function reset() {
      currentAssets.value      = 0;
      currentLiabilities.value = 0;
      inventory.value          = 0;
      totalLiabilities.value   = 0;
      totalEquity.value        = 0;
      netIncome.value          = 0;
      calculated.value         = false;
    }

    return {
      currentAssets,
      currentLiabilities,
      inventory,
      totalLiabilities,
      totalEquity,
      netIncome,
      calculated,
      currentRatio,
      quickRatio,
      debtEquityRatio,
      returnOnEquity,
      calculate,
      reset
    };
  }
};
</script>

<style scoped>
.button-row {
  margin-top: 1.5rem;
}
/* space between the two buttons */
.button-row > * + * {
  margin-left: 1rem;
}
/* results grid: 2 columns on md+, 1 on small screens */
.results-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
@media (min-width: 768px) {
  .results-grid {
    grid-template-columns: 1fr 1fr;
  }
}
.result-card {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
</style>
